import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { DrillPlanAssignment } from '@/components/drill-planner/types/drill-plan-types';
import { formatDateLocale, safeToDate } from '@/lib/utils';
import { PlusCircle } from 'lucide-react';

interface ClientProgramsTabProps {
  assignments: DrillPlanAssignment[];
  loading: boolean;
  clientId: string;
}

export function ClientProgramsTab({ assignments, loading, clientId }: ClientProgramsTabProps) {
  return (
    <Card className="border-none shadow-md" data-testid="client-programs-tab">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div>
          <CardTitle>Programs</CardTitle>
          <CardDescription>Drill plan assignments for this client</CardDescription>
        </div>
        <Button size="sm" asChild>
          <Link to={`/drill-planner?clientId=${clientId}`}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Assign plan
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading programs…</p>
        ) : assignments.length === 0 ? (
          <div className="text-center py-6 space-y-3">
            <p className="text-sm text-muted-foreground">No drill plans assigned yet.</p>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/drill-planner?clientId=${clientId}`}>Open drill planner</Link>
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Assigned</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignments.map((assignment) => {
                const assignedAt = safeToDate(assignment.createdAt);
                return (
                  <TableRow key={assignment.id}>
                    <TableCell className="font-medium">
                      {assignment.drillPlanName || assignment.drillPlanDefinitionId}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {assignedAt
                        ? formatDateLocale(assignedAt, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })
                        : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
